import { useState } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import { TextInput } from "components-design-system"
import MeasureCategory from "../MeasureCategory/MeasureCategory"
import RowActions from "../../../components/RowActions/RowActions"
import CharCounter from "components/CharCounter/CharCounter"
import CellValidation from "../../../components/CellValidation/CellValidation"

// context
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"

// utils
import { moveRowDownHelper, moveRowUpHelper } from "../../helpers/helpers"

const MeasureRow = ({ row, rowIndex, measure, measureIndex, outcomeData, setOutcomeData, showUnitsAnalyzed }) => {
  const [showActions, setShowActions] = useState(false)

  const { readOnly, formView } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      formView: state.formView,
    }))
  )

  const { tableView } = useResultsTableViewStore()

  const handleTitle = (e) => {
    let tempData = { ...outcomeData }

    Object.keys(tempData).forEach((registry) => {
      let foundOMIndex = tempData[registry].outcome_measures.findIndex((om) => om.id === measure.id)
      if (foundOMIndex !== -1) {
        let om = tempData[registry].outcome_measures[foundOMIndex]
        if (om.measure_rows) {
          let foundRowIndex = om.measure_rows.findIndex((measureRow) => measureRow.id === row.id)
          if (foundRowIndex !== -1) {
            om.measure_rows[foundRowIndex].title = e
          }
        }
      }
    })

    setOutcomeData(tempData)
  }

  const handleDeleteRow = (applyAll) => {
    let tempData = { ...outcomeData }

    Object.keys(tempData).forEach((registry) => {
      if (applyAll || registry === tableView) {
        let foundBMIndex = tempData[registry].outcome_measures.findIndex((om) => om.id === measure.id)
        if (foundBMIndex !== -1) {
          let om = tempData[registry].outcome_measures[foundBMIndex]
          if (om.measure_rows) {
            let foundRowIndex = om.measure_rows.findIndex((measureRow) => measureRow.id === row.id)
            if (foundRowIndex !== -1) {
              om.measure_rows.splice(foundRowIndex, 1)
            }
          }
        }
      }
    })

    setOutcomeData({ ...tempData })
  }

  const handleUp = () => {
    let tempData = { ...outcomeData }
    Object.keys(tempData).forEach((registry) => {
      let foundOMIndex = outcomeData[registry].outcome_measures.findIndex((om) => om.id === measure.id)
      if (foundOMIndex !== -1) {
        if (tempData[registry].outcome_measures[foundOMIndex]?.measure_rows) {
          let foundRowIndex = tempData[registry].outcome_measures[foundOMIndex]?.measure_rows.findIndex(
            (measureRow) => measureRow.id === row.id
          )
          if (foundRowIndex !== -1 && foundRowIndex !== 0) {
            moveRowUpHelper(tempData, registry, foundOMIndex, foundRowIndex)
          }
        }
      }
    })

    setOutcomeData({ ...tempData })
  }

  const handleDown = () => {
    let tempData = { ...outcomeData }
    Object.keys(tempData).forEach((registry) => {
      let foundOMIndex = outcomeData[registry].outcome_measures.findIndex((om) => om.id === measure.id)
      if (foundOMIndex !== -1) {
        if (tempData[registry].outcome_measures[foundOMIndex]?.measure_rows) {
          let foundRowIndex = tempData[registry].outcome_measures[foundOMIndex]?.measure_rows.findIndex(
            (measureRow) => measureRow.id === row.id
          )
          if (
            foundRowIndex !== -1 &&
            foundRowIndex !== tempData[registry].outcome_measures[foundOMIndex].measure_rows.length - 1
          ) {
            moveRowDownHelper(tempData, registry, foundOMIndex, foundRowIndex)
          }
        }
      }
    })

    setOutcomeData({ ...tempData })
  }

  return (
    <>
      {row.categories.map((category, catIndex) => {
        if (catIndex === 0) {
          return (
            <tr
              key={row?.id + "-" + category?.id}
              onMouseEnter={() => setShowActions(true)}
              onMouseLeave={() => setShowActions(false)}
            >
              <td
                className="col-sm"
                rowSpan={row.categories.length}
              >
                <TextInput
                  type="textarea"
                  label="Row title"
                  readOnly={readOnly}
                  onChange={(e) => handleTitle(e.target.value)}
                  tooltip={
                    tableView === "eudract" &&
                    "Note: Row titles will be combined with Category titles for EudraCT submissions. e.g. Row 1 Category 1, Row 1 Category 2, Row 2 Category 1, Row 2 Category 2, etc..."
                  }
                  value={row.title}
                />
                <div className="om-arm-actions">
                  {tableView === "ctg" && formView.id !== "global" && !readOnly && (
                    <div className="char-counter-container">
                      <CharCounter
                        value={row.title || ""}
                        maxChars={100}
                      />
                    </div>
                  )}
                  <RowActions
                    showActions={showActions}
                    setShowActions={setShowActions}
                    showDown={rowIndex !== measure.measure_rows.length - 1}
                    showUp={rowIndex !== 0}
                    handleUp={handleUp}
                    handleDown={handleDown}
                    handleDelete={handleDeleteRow}
                    canDelete={measure.measure_rows.length > 1}
                    deleteModalTitle="Delete row?"
                    jpath={`OutcomeRptGroup.om-${measure.id}-row-${row.id}`}
                  />
                </div>
                <CellValidation
                  table="outcome"
                  errorKey={`results_outcome_measures_outcome_measures_table_${tableView}_outcome_measures_${
                    measureIndex + 1
                  }_rows_${rowIndex + 1}`}
                />
              </td>
              <MeasureCategory
                row={row}
                measure={measure}
                measureIndex={measureIndex}
                category={category}
                catIndex={catIndex}
                rowIndex={rowIndex}
                showUnitsAnalyzed={showUnitsAnalyzed}
                outcomeData={outcomeData}
                setOutcomeData={setOutcomeData}
              />
            </tr>
          )
        } else {
          return (
            <tr key={row?.id + "-" + category?.id}>
              <MeasureCategory
                row={row}
                measure={measure}
                measureIndex={measureIndex}
                category={category}
                catIndex={catIndex}
                rowIndex={rowIndex}
                showUnitsAnalyzed={showUnitsAnalyzed}
                outcomeData={outcomeData}
                setOutcomeData={setOutcomeData}
              />
            </tr>
          )
        }
      })}
    </>
  )
}

export default MeasureRow
